import { isBuiltInFont } from '@/utils/fontAssetUtils';
import type { Node } from '@/types/node';
import type { CompositorNodeV3 } from '@/types/nodes/compositor';
import type { BuiltInFont, Font, Layer } from 'web';

export type MissingCustomFontFace = {
  faceKey: string;
  fontFamilyKey: string;
  weight: number;
  isItalic: boolean;
};

export type MissingCustomFontFamily = {
  fontFamilyKey: string;
  faces: MissingCustomFontFace[];
};

/**
 * Workspace uploads store the family as `{workspaceId}_{familyKey}` (`namespaceByWorkspace`).
 * The id is a UUID; the remainder is the compact family key.
 */
const WORKSPACE_FONT_FAMILY_KEY_PREFIX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}_/i;

/** Drops a leading `{workspaceId}_` from a family key. Keys without that prefix are unchanged. */
export function stripWorkspaceIdFromFontFamilyKey(fontFamilyKey: string): string {
  const stripped = fontFamilyKey.replace(WORKSPACE_FONT_FAMILY_KEY_PREFIX, '');
  return stripped.length > 0 ? stripped : fontFamilyKey;
}

export type FontStyleCombination = { weight: number; isItalic: boolean };

/** One workspace-uploaded family as shown in compositor / missing-font pickers. */
export type WorkspaceFontFamily = {
  fontFamilyKey: string;
  displayName: string;
  /** Distinct (weight, italic) faces uploaded for this family; omitted when the caller only needs identity. */
  styleCombinations?: FontStyleCombination[];
};

/** Minimal row used to group workspace font uploads into families and style combinations. */
export type WorkspaceFontFaceRow = {
  fontFamilyKey: string;
  displayName?: string;
  weight: number;
  isItalic: boolean;
};

/** Canonical key for a workspace font face (family + weight + italic), matching recipe font API rows. */
export function makeWorkspaceFontFaceKey(fontFamilyKey: string, weight: number, isItalic: boolean): string {
  return JSON.stringify([fontFamilyKey, Number(weight), Boolean(isItalic)]);
}

/**
 * Family key for a text layer's font: the built-in name, or the custom family. For custom fonts
 * this tolerates pre-rename layers whose data still stores the family on `resourceKey` (mirrors the
 * Rust `#[serde(alias = "resourceKey")]` on `CustomFont.fontFamilyKey`) so client reads of
 * un-migrated workflow data resolve the family too.
 */
export function getFontFamilyKey(font: Font): string {
  if (font.type === 'built_in') {
    return font.data;
  }
  // Custom: tolerate pre-rename layers whose data stores the family on `resourceKey`.
  const data = font.data as { fontFamilyKey?: string; resourceKey?: string };
  return data.fontFamilyKey ?? data.resourceKey ?? '';
}

/**
 * Compositor `Font` for a family dropdown value: built-ins stay `built_in`, workspace uploads are `custom`.
 */
export function fontFromFamilyKey(fontFamilyKey: string): Font {
  if (isBuiltInFont(fontFamilyKey)) {
    return { type: 'built_in', data: fontFamilyKey as BuiltInFont };
  }
  return { type: 'custom', data: { fontFamilyKey, index: 0 } };
}

export function encodeFontStyleValue(weight: number, isItalic: boolean): string {
  return `${weight}\u001f${isItalic ? '1' : '0'}`;
}

/** Distinct (weight, italic) faces per `fontFamilyKey` from workspace font rows. */
export function buildWorkspaceFontStyleCombinationsByFamily(
  fonts: readonly WorkspaceFontFaceRow[],
): ReadonlyMap<string, FontStyleCombination[]> {
  const byFamily = new Map<string, FontStyleCombination[]>();
  const seenKeys = new Map<string, Set<string>>();
  for (const f of fonts) {
    const enc = encodeFontStyleValue(f.weight, f.isItalic);
    let seen = seenKeys.get(f.fontFamilyKey);
    if (!seen) {
      seen = new Set();
      seenKeys.set(f.fontFamilyKey, seen);
      byFamily.set(f.fontFamilyKey, []);
    }
    if (seen.has(enc)) {
      continue;
    }
    seen.add(enc);
    byFamily.get(f.fontFamilyKey)!.push({ weight: f.weight, isItalic: f.isItalic });
  }
  for (const combos of byFamily.values()) {
    combos.sort((a, b) => (a.weight !== b.weight ? a.weight - b.weight : Number(a.isItalic) - Number(b.isItalic)));
  }
  return byFamily;
}

/**
 * Whether uploaded workspace families may appear in compositor / missing-font pickers.
 * `workspace-custom-fonts` surfaces uploads; `workspace-custom-fonts-wasm` populates the engine
 * catalog. Both must be on — otherwise selecting a custom family falls back to DMSans with empty
 * weight/style options.
 */
export function canOfferCustomFontChoices(
  isWorkspaceCustomFontsEnabled: boolean | undefined,
  isCustomFontsWasmEnabled: boolean | undefined,
): boolean {
  return isWorkspaceCustomFontsEnabled === true && isCustomFontsWasmEnabled === true;
}

/**
 * Distinct families from workspace font rows, with the (weight, italic) faces each family has.
 * First `displayName` seen for a family wins.
 */
export function buildWorkspaceFontFamilies(fonts: readonly WorkspaceFontFaceRow[]): WorkspaceFontFamily[] {
  const combinationsByFamily = buildWorkspaceFontStyleCombinationsByFamily(fonts);
  const families: WorkspaceFontFamily[] = [];
  const seen = new Set<string>();
  for (const font of fonts) {
    if (seen.has(font.fontFamilyKey)) {
      continue;
    }
    seen.add(font.fontFamilyKey);
    families.push({
      fontFamilyKey: font.fontFamilyKey,
      displayName: font.displayName ?? font.fontFamilyKey,
      styleCombinations: combinationsByFamily.get(font.fontFamilyKey) ?? [],
    });
  }
  return families;
}

/** Distinct weights in family-upload order (already sorted by {@link buildWorkspaceFontStyleCombinationsByFamily}). */
export function distinctWeightsFromStyleCombinations(combos: readonly FontStyleCombination[]): number[] {
  const weights: number[] = [];
  const seen = new Set<number>();
  for (const combo of combos) {
    if (seen.has(combo.weight)) {
      continue;
    }
    seen.add(combo.weight);
    weights.push(combo.weight);
  }
  return weights;
}

/**
 * Slants uploaded at `weight`, in the same order as {@link italicOptionsFromStyleCombinations}.
 * Empty when that weight has no listed face — built-ins, and a weight the family was not
 * uploaded at — so the caller can keep its own answer.
 */
export function italicOptionsAtWeight(combos: readonly FontStyleCombination[], weight: number): boolean[] {
  return italicOptionsFromStyleCombinations(combos.filter((combo) => Math.abs(combo.weight - weight) < 0.0001));
}

/**
 * Italic options implied by uploaded cuts. Order matches the engine catalog:
 * both → `[true, false]`, italic-only → `[true]`, upright-only → `[false]`.
 */
export function italicOptionsFromStyleCombinations(combos: readonly FontStyleCombination[]): boolean[] {
  const hasItalic = combos.some((c) => c.isItalic);
  const hasUpright = combos.some((c) => !c.isItalic);
  if (hasItalic && hasUpright) {
    return [true, false];
  }
  if (hasItalic) {
    return [true];
  }
  if (hasUpright) {
    return [false];
  }
  return [];
}

/** Uploaded faces for a family; empty for one the workspace has no uploads listed for. */
export function styleCombinationsForFamily(
  workspaceFonts: readonly WorkspaceFontFamily[],
  fontFamilyKey: string,
): readonly FontStyleCombination[] {
  return workspaceFonts.find((family) => family.fontFamilyKey === fontFamilyKey)?.styleCombinations ?? [];
}

/**
 * The face a layer lands on when its font changes and the family's uploaded faces are `combos`:
 * `desired` when the family has it, and otherwise the closest face it does have. `hold` is the axis
 * the change was made on, which is the one kept: picking a weight the family has in one slant only
 * lands on that slant, and picking a slant it has at other weights only lands on the nearest of
 * them. `null` when no faces are known — built-ins, and families whose uploads have not loaded —
 * which leaves the layer's weight and slant alone.
 */
export function nearestFontStyleCombination(
  combos: readonly FontStyleCombination[],
  desired: FontStyleCombination,
  hold: 'slant' | 'weight' = 'slant',
): FontStyleCombination | null {
  const held = combos.filter((combo) =>
    hold === 'slant' ? combo.isItalic === desired.isItalic : combo.weight === desired.weight,
  );
  const candidates = held.length > 0 ? held : combos;
  let nearest: FontStyleCombination | null = null;
  for (const combo of candidates) {
    if (combo.weight === desired.weight && combo.isItalic === desired.isItalic) {
      return combo;
    }
    if (nearest === null || Math.abs(combo.weight - desired.weight) < Math.abs(nearest.weight - desired.weight)) {
      nearest = combo;
    }
  }
  return nearest;
}

export function decodeFontStyleValue(value: string): FontStyleCombination | null {
  const parts = value.split('\u001f');
  if (parts.length !== 2) {
    return null;
  }
  const weight = Number(parts[0]);
  const isItalic = parts[1] === '1';
  if (!Number.isFinite(weight)) {
    return null;
  }
  return { weight, isItalic };
}

function tryCollectMissingCustomFontFace(
  layer: Layer,
  allowedWorkspaceCustomFontFaceKeys: ReadonlySet<string>,
): MissingCustomFontFace | null {
  if (layer.kind.type !== 'text') {
    return null;
  }
  const font = layer.kind.fontOptions.font;
  const { weight, italic: isItalic } = layer.kind.fontOptions;
  const fontFamilyKey = getFontFamilyKey(font);
  const faceKey = makeWorkspaceFontFaceKey(fontFamilyKey, weight, isItalic);
  // Built-in fonts (base and company) always render, so they can't be "missing"; only custom
  // uploads must be present in the user's allowed workspace font list.
  if (isBuiltInFont(fontFamilyKey) || allowedWorkspaceCustomFontFaceKeys.has(faceKey)) {
    return null;
  }

  return { faceKey, fontFamilyKey, weight, isItalic };
}

/**
 * Groups missing custom font faces (not present in the user's allowed workspace font list) by `resourceKey`.
 * Built-in fonts are ignored — only custom uploads can be workspace-backed.
 */
export function collectMissingWorkspaceFontFamiliesFromNodes(
  nodes: Node[],
  allowedWorkspaceCustomFontFaceKeys: ReadonlySet<string>,
): MissingCustomFontFamily[] {
  const faceByKey = new Map<string, MissingCustomFontFace>();
  for (const node of nodes) {
    if (node.type !== 'compv3') {
      continue;
    }
    const compositor = node.data as CompositorNodeV3;
    const layers = compositor?.data?.layers;
    if (!layers || typeof layers !== 'object') {
      continue;
    }
    for (const layer of Object.values(layers)) {
      const face = tryCollectMissingCustomFontFace(layer, allowedWorkspaceCustomFontFaceKeys);
      if (!face) {
        continue;
      }
      faceByKey.set(face.faceKey, face);
    }
  }
  const byFamily = new Map<string, MissingCustomFontFace[]>();
  for (const face of faceByKey.values()) {
    const list = byFamily.get(face.fontFamilyKey) ?? [];
    list.push(face);
    byFamily.set(face.fontFamilyKey, list);
  }
  return [...byFamily.entries()]
    .map(([fontFamilyKey, faces]) => ({
      fontFamilyKey,
      faces: faces.sort((a, b) => a.faceKey.localeCompare(b.faceKey)),
    }))
    .sort((a, b) => a.fontFamilyKey.localeCompare(b.fontFamilyKey));
}
